import { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Chip } from "../lux";
import { Field, MoneyInput, LocationInput, MoreOptions } from "./pickers";
import WhoGetsWhat from "./WhoGetsWhat";
import { PackagePicker, CustomAddons } from "./PackagePicker";
import { DayHoursField } from "./DayHoursField";
import {
  RENTAL_CATEGORY, CHAUFFEUR_SERVICE, TAX_RATE, PRICING_MODE,
  chargesHourly, hasPackage, remainingBalance, hourPeriods, inclusiveDays, billableServiceHours,
} from "../../pages/bookingFields";
import { AirplaneTilt } from "@phosphor-icons/react";

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StepDetails({ formType, form, setForm, errors, ctx }) {
  const { moneyVisible, vehicles, recentLocations, flightNo, setFlightNo } = ctx;
  const isRental = formType === "rental";
  const hourly = isRental || chargesHourly(form);
  const packagePriced = hasPackage(form, formType);
  const [extra, setExtra] = useState(() => new Set(
    ["delivery", "other_charges", "overnight_fee"].filter((k) => Number(form[k]) > 0)
  ));

  // What the Total hint spells out, so the operator can see the pile.
  const windowHours = Math.round(((Date.parse(form.end_datetime) - Date.parse(form.start_datetime)) / 3600000) * 10) / 10;
  const standbySplit = formType === "chauffeur" && Number(form.standby_hours) > 0 && windowHours > 0;
  // SALES-029: the day-count suggestions the chips offer, plus the auto hours
  // behind the "Use calculated" link. WHEN Days follows the dates is the
  // wizard's derive effect's job, not these values'.
  const days24 = hourPeriods(form.start_datetime, form.end_datetime);
  const daysCal = inclusiveDays(form.start_datetime, form.end_datetime);
  const autoHours = billableServiceHours(windowHours, form, formType);
  const showHalfDay = windowHours > 0 && windowHours < 24;
  const autoParts = isRental
    ? ["Days × Daily rate", "Delivery", "Overnight fee", "Other charges"]
    : [
        packagePriced ? "Package" : "Hours × Rate",
        ...(formType === "chauffeur" ? ["Standby"] : []),
        ...(formType === "tour" ? ["Custom add-ons"] : []),
        "Delivery", "Other charges",
      ];

  const toggleExtra = (key) => {
    setExtra((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); setForm((f) => ({ ...f, [key]: 0 })); }
      else next.add(key);
      return next;
    });
  };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const num = (v) => Number(v || 0);
  const subtotal = num(form.total);
  const tax = form.tax_applied ? subtotal * TAX_RATE : 0;

  return (
    <div className="space-y-8">
      {/* Category / service */}
      {isRental ? (
        <Field label="Category" required error={errors.booking_category}>
          <div className="flex gap-2 flex-wrap" data-testid="bookings-field-booking_category">
            {RENTAL_CATEGORY.map((o) => (
              <Chip key={o.value} active={form.booking_category === o.value} onClick={() => set({ booking_category: o.value })} testid={`category-${o.value}`}>
                {o.label}
              </Chip>
            ))}
          </div>
        </Field>
      ) : (
        <Field label="Service type">
          <div className="flex gap-2 flex-wrap" data-testid="bookings-field-service_type_detail">
            {CHAUFFEUR_SERVICE.map((o) => (
              <Chip key={o.value} active={form.service_type_detail === o.value} onClick={() => set({ service_type_detail: o.value })} testid={`service-${o.value}`}>
                {o.label}
              </Chip>
            ))}
          </div>
        </Field>
      )}

      {formType === "tour" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Tour / package name">
            <Input value={form.tour_name ?? ""} onChange={(e) => set({ tour_name: e.target.value })} placeholder="e.g. Niagara day tour" data-testid="bookings-field-tour_name" />
          </Field>
          <Field label="Passengers">
            <Input type="number" min="1" value={form.passenger_count ?? ""} onChange={(e) => set({ passenger_count: e.target.value })} data-testid="bookings-field-passenger_count" />
          </Field>
        </div>
      )}

      {formType === "tour" && (
        <div className="space-y-5">
          <Field label="Package" hint="Pick a package to price the day off it instead of Hours × Rate.">
            <PackagePicker form={form} setForm={setForm} />
          </Field>
          <Field label="Custom add-ons" hint="Anything the client asked for that isn't in the package.">
            <CustomAddons value={form.custom_addons} onChange={(v) => set({ custom_addons: v })} />
          </Field>
        </div>
      )}

      {/* Locations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <LocationInput
          label={isRental ? "Pickup / delivery location" : "Pickup location"}
          required={!isRental}
          error={errors.start_location}
          value={form.start_location}
          onChange={(v) => set({ start_location: v })}
          recents={recentLocations}
          placeholder="Address, hotel, airport…"
          testid="bookings-field-start_location"
        />
        <LocationInput
          label={isRental ? "Return location" : "Drop-off location"}
          required={!isRental}
          error={errors.end_location}
          value={form.end_location}
          onChange={(v) => set({ end_location: v })}
          recents={recentLocations}
          placeholder="Address, hotel, airport…"
          testid="bookings-field-end_location"
        />
      </div>

      {!isRental && form.service_type_detail === "airport" && (
        <Field label="Flight number" hint="Saved with the booking notes so dispatch can track the arrival.">
          <div className="relative">
            <AirplaneTilt size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
            <Input value={flightNo} onChange={(e) => setFlightNo(e.target.value)} placeholder="e.g. AC 848" className="pl-10" data-testid="bookings-field-flight_number" />
          </div>
        </Field>
      )}

      {!isRental && (
        <Field label="Stops / itinerary">
          <Textarea rows={3} value={form.in_between_stops ?? ""} onChange={(e) => set({ in_between_stops: e.target.value })} placeholder="One stop per line…" data-testid="bookings-field-in_between_stops" />
        </Field>
      )}

      {/* Add-ons */}
      <div>
        <div className="label-mono mb-3">Add-ons</div>
        <div className="flex gap-2 flex-wrap" data-testid="wizard-addon-chips">
          <Chip active={extra.has("delivery")} onClick={() => toggleExtra("delivery")} testid="addon-delivery">Delivery fee</Chip>
          <Chip active={extra.has("other_charges")} onClick={() => toggleExtra("other_charges")} testid="addon-other_charges">Extra charges</Chip>
          {isRental && (
            <Chip active={extra.has("overnight_fee")} onClick={() => toggleExtra("overnight_fee")} testid="addon-overnight_fee">Overnight fee</Chip>
          )}
          {isRental && (
            <Chip active={String(form.gas_filled || "").toLowerCase() === "yes"} onClick={() => set({ gas_filled: String(form.gas_filled || "").toLowerCase() === "yes" ? "" : "Yes" })} testid="addon-gas_filled">
              Gas filled
            </Chip>
          )}
          <Chip active={!!form.tax_applied} onClick={() => set({ tax_applied: !form.tax_applied })} testid="booking-tax-toggle">
            Add HST ({Math.round(TAX_RATE * 100)}%)
          </Chip>
        </div>
      </div>

      {/* Pricing */}
      <div className="space-y-5">
        <div className="label-mono">Pricing</div>
        {!isRental && (
          <Field label="How is this charged?" required>
            <div className="flex gap-2 flex-wrap" data-testid="bookings-field-pricing_mode">
              {PRICING_MODE.map((o) => (
                <Chip
                  key={o.value}
                  active={(form.pricing_mode || "hourly") === o.value}
                  onClick={() => set({ pricing_mode: o.value })}
                  testid={`pricing-mode-${o.value}`}
                >
                  {o.label}
                </Chip>
              ))}
            </div>
          </Field>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {isRental ? (
            <>
              <Field
                label="Days"
                hint={form.rental_days_manual
                  ? "Your number — tap a suggestion or clear the box to go back to automatic."
                  : "Suggested from the dates (24-hour days). Tap a chip or type any number (steps of 0.5)."}
              >
                <DayHoursField
                  testid="bookings-field-rental_days"
                  value={form.rental_days}
                  manual={!!form.rental_days_manual}
                  autoValue={days24}
                  step="0.5" min="0.5"
                  onChange={(v, manual) => set({ rental_days: v, rental_days_manual: manual })}
                  chips={[
                    { key: "24h", label: `24 hours: ${days24}`, value: days24, manual: false, active: !form.rental_days_manual },
                    { key: "calendar", label: `Calendar days: ${daysCal}`, value: daysCal, manual: true, active: !!form.rental_days_manual && Number(form.rental_days) === daysCal },
                    ...(showHalfDay ? [{ key: "half", label: "Half day", value: 0.5, manual: true, active: !!form.rental_days_manual && Number(form.rental_days) === 0.5 }] : []),
                  ]}
                />
              </Field>
              <Field label="Daily rate" required error={errors.rental_rate}>
                <MoneyInput value={form.rental_rate} onChange={(v) => set({ rental_rate: v })} testid="bookings-field-rental_rate" />
              </Field>
            </>
          ) : (
            <>
              <Field
                label="Service hours"
                hint={form.service_hours_manual
                  ? undefined
                  : standbySplit
                    ? `Billable hours: ${windowHours}h window − ${Number(form.standby_hours) || 0}h standby. Standby bills at its own rate.`
                    : "Auto-calculated from the time window."}
              >
                <DayHoursField
                  testid="bookings-field-service_hours"
                  value={form.service_hours}
                  manual={!!form.service_hours_manual}
                  autoValue={autoHours}
                  step="0.1" min="0"
                  onChange={(v, manual) => set({ service_hours: v, service_hours_manual: manual })}
                  useCalculatedLabel={form.service_hours_manual ? `Use calculated: ${autoHours} hours` : undefined}
                />
              </Field>
              {hourly && (
                <Field
                  label="Hourly rate"
                  required
                  error={errors.hourly_rate}
                  hint={packagePriced ? "Ignored while a package is selected — the package sets the price." : undefined}
                >
                  <MoneyInput value={form.hourly_rate} onChange={(v) => set({ hourly_rate: v })} testid="bookings-field-hourly_rate" />
                </Field>
              )}
            </>
          )}
          {formType === "chauffeur" && (
            <>
              <Field label="Standby hours" hint={hourly ? "Comes out of the billable service hours — the client isn't charged twice for the same hour." : "Recorded only — whole-job pricing doesn't add it."}>
                <Input type="number" step="0.1" min="0" value={form.standby_hours ?? ""} onChange={(e) => set({ standby_hours: e.target.value })} data-testid="bookings-field-standby_hours" />
              </Field>
              <Field label="Standby rate (per hour)">
                <MoneyInput value={form.standby_rate} onChange={(v) => set({ standby_rate: v })} testid="bookings-field-standby_rate" />
              </Field>
            </>
          )}
          {extra.has("delivery") && (
            <Field label="Delivery charge">
              <MoneyInput value={form.delivery} onChange={(v) => set({ delivery: v })} testid="bookings-field-delivery" />
            </Field>
          )}
          {extra.has("other_charges") && (
            <Field label="Other charges">
              <MoneyInput value={form.other_charges} onChange={(v) => set({ other_charges: v })} testid="bookings-field-other_charges" />
            </Field>
          )}
          {isRental && extra.has("overnight_fee") && (
            <Field label="Overnight fee" hint="Varies by car — type the amount for this booking.">
              <MoneyInput value={form.overnight_fee} onChange={(v) => set({ overnight_fee: v })} testid="bookings-field-overnight_fee" />
            </Field>
          )}
          <Field
            label="Total"
            required
            error={errors.total}
            hint={!hourly
              ? "One price for the whole job — type it in. Nothing is added on top."
              : form.total_manual
                ? "Manual override — clear the field to re-enable auto."
                : `Auto = ${autoParts.join(" + ")}`}
          >
            <MoneyInput value={form.total} onChange={(v) => set({ total: v, total_manual: v !== "" })} testid="bookings-field-total" />
          </Field>
          {isRental ? (
            <Field label="Deposit amount" required error={errors.deposit_amount}>
              <MoneyInput value={form.deposit_amount} onChange={(v) => set({ deposit_amount: v })} testid="bookings-field-deposit_amount" />
            </Field>
          ) : (
            <>
              <Field label="Deposit sent" required error={errors.depo_sent} hint="Enter 0 if no deposit was taken.">
                <MoneyInput value={form.depo_sent} onChange={(v) => set({ depo_sent: v })} testid="bookings-field-depo_sent" />
              </Field>
              <Field label="Remaining balance" hint="Total − deposit, kept in step automatically.">
                <Input
                  readOnly
                  value={fmt(remainingBalance(form))}
                  className="text-muted-foreground"
                  data-testid="bookings-field-remaining_balance"
                />
              </Field>
            </>
          )}
        </div>

        {moneyVisible && (
          <div className="border border-primary/30 bg-primary/[0.04] rounded-2xl p-5" data-testid="wizard-total-summary">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="tnum">{fmt(subtotal)}</span></div>
            {form.tax_applied && (
              <div className="flex justify-between text-sm mt-1.5"><span className="text-muted-foreground">HST ({Math.round(TAX_RATE * 100)}%)</span><span className="tnum">{fmt(tax)}</span></div>
            )}
            <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-border">
              <span className="label-mono">Client pays</span>
              <span className="font-heading text-2xl money" data-testid="booking-total-with-tax">{fmt(subtotal + tax)}</span>
            </div>
          </div>
        )}
      </div>

      <Field label="Notes">
        <Textarea rows={3} value={form.notes ?? ""} onChange={(e) => set({ notes: e.target.value })} placeholder="Anything dispatch or the desk should know…" data-testid="bookings-field-notes" />
      </Field>

      {/* Rarely-used fields stay reachable */}
      <MoreOptions testid="wizard-more-options">
        <Field label="Booking date">
          <Input type="date" value={form.booking_date ?? ""} onChange={(e) => set({ booking_date: e.target.value })} data-testid="bookings-field-booking_date" />
        </Field>
        {isRental && (
          <>
            <Field label="Gas filled (free text)">
              <Input value={form.gas_filled ?? ""} onChange={(e) => set({ gas_filled: e.target.value })} placeholder="Yes / No / level" data-testid="bookings-field-gas_filled" />
            </Field>
            <Field label="Start KM">
              <Input type="number" value={form.start_km ?? ""} onChange={(e) => set({ start_km: e.target.value })} data-testid="bookings-field-start_km" />
            </Field>
            <Field label="End KM">
              <Input type="number" value={form.end_km ?? ""} onChange={(e) => set({ end_km: e.target.value })} data-testid="bookings-field-end_km" />
            </Field>
          </>
        )}
        {!isRental && moneyVisible && (
          <>
            <Field label="Driver cost">
              <MoneyInput value={form.driver_cost} onChange={(v) => set({ driver_cost: v })} testid="bookings-field-driver_cost" />
            </Field>
            <Field label="Gas cost">
              <MoneyInput value={form.gas_cost} onChange={(v) => set({ gas_cost: v })} testid="bookings-field-gas_cost" />
            </Field>
            <Field label="Other costs">
              <MoneyInput value={form.other_costs} onChange={(v) => set({ other_costs: v })} testid="bookings-field-other_costs" />
            </Field>
            <Field label="Owner base rate" hint="3rd-party base-rate cars only.">
              <MoneyInput value={form.owner_base_rate} onChange={(v) => set({ owner_base_rate: v })} testid="bookings-field-owner_base_rate" />
            </Field>
            <Field label="Owner drove their own car">
              <Chip active={!!form.owner_drove} onClick={() => set({ owner_drove: !form.owner_drove })} testid="bookings-field-owner_drove">
                {form.owner_drove ? "Yes — driver cost paid to owner" : "No"}
              </Chip>
            </Field>
          </>
        )}
      </MoreOptions>

      {!isRental && moneyVisible && form.vehicle_id && <WhoGetsWhat form={form} vehicles={vehicles} />}
    </div>
  );
}
